/**
 * RiskPage — Phase 3A UI.
 *
 * Displays every computed risk assessment across the insurer's portfolio,
 * with a filter row (band + broker) plus a detail drawer for reviewing the
 * factor breakdown, current override (if any) and override history. The
 * override form enforces the same mandatory-reason contract as the backend
 * so operators never see a validation surprise mid-review.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { SearchPicker } from '@/components/SearchPicker';
import { toast } from 'sonner';
import { RefreshCcw, ShieldAlert, Undo2, Info, X, Eraser, Calendar, MapPin, FileText, Download, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';

type Band = 'low' | 'medium' | 'high' | 'critical';

interface Factor {
  question_code: string;
  question_text: string;
  answer: unknown;
  weight: number;
  contribution: number;
  passed: boolean;
  notes: string;
  is_abm_row?: boolean;
  abm_match_confidence?: number;
  abm_unknown_types?: string[];
  abm_cash_value_zar?: number | null;
  abm_kind?: 'notes' | 'coin' | null;
}

interface Override {
  id: string;
  assessment_id: string;
  from_band: Band;
  to_band: Band;
  from_score: number;
  to_score: number;
  reason: string;
  actor_id: string;
  actor_email: string;
  created_at: string;
}

interface ClusterDetail {
  cluster_id: 'A' | 'B' | 'C';
  cluster_name: string;
  raw_points: number;
  max_points_additive: number;
  bad_answer_count: number;
  multiplier: number;
  compounded_points: number;
}

interface AbmBreakdown {
  engine: string;
  clusters: ClusterDetail[];
  security: { compounded_points: number; compounded_max: number; score: number; weight: number };
  power_backup: { yes_count: number; points: number; max_points: number; score: number; description: string; weight: number };
  cash_value: { cash_value_zar: number | null; resolved_model: string | null; points: number; max_points: number; score: number; description: string; weight: number; match_confidence?: number; match_sources?: string[]; unknown_abm_types?: string[] };
  weighted_base: number;
  weak_category_count: number;
  cross_category_bonus: number;
  total_score: number;
  band: Band;
}

interface Assessment {
  id: string;
  result_id: string;
  instruction_id: string;
  template_id: string;
  template_version: number;
  template_name: string;
  insurer_id: string | null;
  broker_id: string | null;
  client_id?: string | null;
  site_id?: string | null;
  site_name?: string | null;
  site_reference: string;
  client_name: string;
  surveyor_name?: string | null;
  submitted_at?: string | null;
  abm_match_confidence?: number | null;
  abm_resolved_model?: string | null;
  abm_unknown_types?: string[];
  raw_score: number;
  max_possible: number;
  normalised_score: number;
  computed_band: Band;
  effective_band: Band;
  effective_score: number;
  is_overridden: boolean;
  override_id: string | null;
  factors: Factor[];
  computed_at: string;
  overrides?: Override[];
  engine?: string;
  breakdown?: AbmBreakdown;
  // Set when the surveyor proceeded outside the site's geofence
  // threshold. Renders a red banner at the top of the review drawer.
  geofence_bypass?: {
    distance_m: number;
    threshold_m: number;
    site_lat?: number | null;
    site_lng?: number | null;
    reason?: string;
    acknowledged_at?: string;
  } | null;
}

interface Summary {
  total: number;
  by_band: { low: number; medium: number; high: number; critical: number };
}

const BAND_STYLE: Record<Band, { badge: 'success' | 'warning' | 'destructive' | 'muted'; label: string; ring: string }> = {
  low: { badge: 'success', label: 'Low', ring: 'ring-emerald-300' },
  medium: { badge: 'warning', label: 'Medium', ring: 'ring-amber-300' },
  high: { badge: 'destructive', label: 'High', ring: 'ring-red-300' },
  critical: { badge: 'destructive', label: 'Critical', ring: 'ring-red-500' },
};

const BandBadge: React.FC<{ band: Band }> = ({ band }) => {
  const s = BAND_STYLE[band] ?? BAND_STYLE.low;
  return (
    <Badge variant={s.badge} data-testid={`risk-band-${band}`}>
      {s.label}
    </Badge>
  );
};

/** ABM match badge — small coloured pill shown in the Risk Ratings row.
 *  ≥ 80% → green ("high confidence"), 40-79% → amber ("review"),
 *  < 40% → red ("needs fix"). Hovers show the resolved model or the
 *  unknown-ABM strings that need adding to the library. */
const AbmMatchBadge: React.FC<{ confidence?: number | null; model?: string | null; unknown?: string[] }> = ({ confidence, model, unknown }) => {
  const c = Math.max(0, Math.min(100, Number(confidence ?? 0)));
  const isUnknown = c === 0 || (unknown && unknown.length > 0 && !model);
  const cls =
    isUnknown ? 'bg-red-100 text-red-800 border-red-300'
    : c >= 80 ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
    : c >= 40 ? 'bg-amber-100 text-amber-800 border-amber-300'
              : 'bg-red-100 text-red-800 border-red-300';
  const tip =
    isUnknown && unknown && unknown.length
      ? `Unknown: ${unknown.join(', ')}`
      : model
        ? `Matched: ${model}`
        : 'No ABM answer captured on this survey';
  return (
    <span
      title={tip}
      className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full border text-[10px] font-tabular font-semibold min-w-[52px] ${cls}`}
      data-testid="abm-match-badge"
    >
      {isUnknown ? '—' : `${c}%`}
    </span>
  );
};



const fmtZAR = (v: number | null): string => v == null ? '—' : `R\u00a0${v.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`;

const AbmBreakdownPanel: React.FC<{ breakdown: AbmBreakdown }> = ({ breakdown }) => {
  return (
    <div className="mb-4 space-y-3" data-testid="abm-breakdown">
      <div className="rounded-md border border-raptor-blue/30 bg-raptor-blue/[0.03] p-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-widest text-raptor-blue font-bold">ABM Site Risk Matrix v3</span>
          <BandBadge band={breakdown.band} />
        </div>
        <div className="text-xs text-raptor-navy/70 font-body">
          Cluster-compounding model &middot; 55% Security / 10% Power / 35% Cash &middot; cross-category bonus applied when 2+ categories score ≥50.
        </div>
      </div>

      {/* Security clusters */}
      <div className="rounded-md border border-border/70 overflow-hidden">
        <div className="px-3 py-2 bg-raptor-neutral/40 flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-raptor-navy/70 font-bold">Security clusters (55% weight)</div>
          <div className="text-sm font-tabular text-raptor-navy">Score: <b>{breakdown.security.score.toFixed(1)}</b> / 100</div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs text-raptor-navy/60">
            <tr>
              <th className="py-1.5 px-3 text-left font-normal">Cluster</th>
              <th className="py-1.5 px-3 text-right font-normal">Raw / Max</th>
              <th className="py-1.5 px-3 text-right font-normal">Bad answers</th>
              <th className="py-1.5 px-3 text-right font-normal">Multiplier</th>
              <th className="py-1.5 px-3 text-right font-normal">Compounded</th>
            </tr>
          </thead>
          <tbody>
            {breakdown.clusters.map((c) => (
              <tr key={c.cluster_id} className="border-t border-border/60" data-testid={`cluster-${c.cluster_id}`}>
                <td className="py-2 px-3">
                  <div className="text-raptor-navy font-medium">{c.cluster_name}</div>
                </td>
                <td className="py-2 px-3 text-right font-tabular">{c.raw_points.toFixed(2)} / {c.max_points_additive}</td>
                <td className="py-2 px-3 text-right font-tabular">
                  <Badge variant={c.bad_answer_count >= 3 ? 'destructive' : c.bad_answer_count >= 2 ? 'warning' : 'muted'}>
                    {c.bad_answer_count}
                  </Badge>
                </td>
                <td className="py-2 px-3 text-right font-tabular">
                  <span className={c.multiplier > 1 ? 'text-raptor-blue font-bold' : 'text-raptor-navy/60'}>
                    ×{c.multiplier.toFixed(2)}
                  </span>
                </td>
                <td className="py-2 px-3 text-right font-tabular font-semibold text-raptor-navy">{c.compounded_points.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border/70 bg-raptor-neutral/30">
              <td className="py-2 px-3 text-xs uppercase tracking-widest text-raptor-navy/70 font-bold">Security total</td>
              <td className="py-2 px-3" />
              <td className="py-2 px-3" />
              <td className="py-2 px-3 text-right text-xs text-raptor-navy/60">of {breakdown.security.compounded_max}</td>
              <td className="py-2 px-3 text-right font-tabular font-bold text-raptor-navy">{breakdown.security.compounded_points.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Power backup + Cash value */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded-md border border-border/70 p-3" data-testid="breakdown-power">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs uppercase tracking-widest text-raptor-navy/70 font-bold">Power backup (10% weight)</div>
            <div className="text-sm font-tabular"><b>{breakdown.power_backup.score.toFixed(1)}</b> / 100</div>
          </div>
          <div className="text-sm text-raptor-navy/70">{breakdown.power_backup.description}</div>
          <div className="text-xs text-raptor-navy/50 mt-1 font-tabular">{breakdown.power_backup.points} of {breakdown.power_backup.max_points} risk pts &middot; {breakdown.power_backup.yes_count}/5 sources present</div>
        </div>
        <div className="rounded-md border border-border/70 p-3" data-testid="breakdown-cash">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs uppercase tracking-widest text-raptor-navy/70 font-bold">Cash value at risk (35% weight)</div>
            <div className="text-sm font-tabular"><b>{breakdown.cash_value.score.toFixed(1)}</b> / 100</div>
          </div>
          <div className="text-sm text-raptor-navy/70">{breakdown.cash_value.description}</div>
          {breakdown.cash_value.resolved_model && (
            <div className="text-xs text-raptor-navy/50 mt-1 flex items-center gap-2 flex-wrap">
              <span>ABM: {breakdown.cash_value.resolved_model}{breakdown.cash_value.cash_value_zar ? ` · ${fmtZAR(breakdown.cash_value.cash_value_zar)}` : ''}</span>
              {typeof breakdown.cash_value.match_confidence === 'number' && (
                <AbmMatchBadge confidence={breakdown.cash_value.match_confidence} model={breakdown.cash_value.resolved_model} />
              )}
            </div>
          )}
          {(breakdown.cash_value.match_sources || []).length > 0 && (
            <div className="text-[10px] text-raptor-navy/50 mt-1 space-y-0.5">
              {breakdown.cash_value.match_sources!.map((s, i) => (
                <div key={i} className="font-tabular">↳ {s}</div>
              ))}
            </div>
          )}
          {(breakdown.cash_value.unknown_abm_types || []).length > 0 && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-[11px] text-red-800">
              <b>Unresolved:</b> {breakdown.cash_value.unknown_abm_types!.join(', ')}.
              Add these to the ABM Library (Settings › Risk Matrices › ABM Library) to score them.
            </div>
          )}
        </div>
      </div>

      {/* Total + bonus */}
      <div className="rounded-md border border-raptor-navy/20 bg-raptor-navy text-white p-3">
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-70">Weighted base</div>
            <div className="font-tabular text-lg font-bold">{breakdown.weighted_base.toFixed(1)}</div>
            <div className="text-[10px] opacity-70">55/10/35 weighted sum</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-70">Cross-category bonus</div>
            <div className={`font-tabular text-lg font-bold ${breakdown.cross_category_bonus > 0 ? 'text-amber-300' : 'opacity-80'}`}>+{breakdown.cross_category_bonus.toFixed(0)}</div>
            <div className="text-[10px] opacity-70">{breakdown.weak_category_count} of 3 categories ≥ 50</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-70">Total score (capped 100)</div>
            <div className="font-tabular text-2xl font-bold">{breakdown.total_score.toFixed(1)}</div>
            <div className="text-[10px] opacity-70">Final band: <b className="uppercase">{breakdown.band}</b></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RiskPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialBand = searchParams.get('band') ?? '';
  const [items, setItems] = useState<Assessment[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [filterBand, setFilterBand] = useState<string>(initialBand);
  const [filterClientId, setFilterClientId] = useState<string>(searchParams.get('client_id') ?? '');
  const [filterSiteId, setFilterSiteId] = useState<string>(searchParams.get('site_id') ?? '');
  const [filterFrom, setFilterFrom] = useState<string>(searchParams.get('from') ?? '');
  const [filterTo, setFilterTo] = useState<string>(searchParams.get('to') ?? '');
  // ABM match confidence filter — client-side because it's a UX bucket over the loaded rows.
  // Buckets: '' = all, 'unknown', 'low' (<40%), 'review' (40–79%), 'good' (≥80%), 'na' (no ABM data).
  const [filterAbm, setFilterAbm] = useState<string>(searchParams.get('abm') ?? '');
  const [clients, setClients] = useState<Array<{ id: string; name: string; trading_name?: string }>>([]);
  const [sites, setSites] = useState<Array<{ id: string; name: string; client_id?: string; site_reference?: string }>>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Assessment | null>(null);
  // "Fix ABM" inline dialog state — opened from the ABM factor row
  const [abmFix, setAbmFix] = useState<{ assessmentId: string; current: string } | null>(null);
  const [viewSurvey, setViewSurvey] = useState<string | null>(null);
  const [overrideBand, setOverrideBand] = useState<Band>('medium');
  const [overrideReason, setOverrideReason] = useState<string>('');
  const [overrideScore, setOverrideScore] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [recomputing, setRecomputing] = useState<boolean>(false);
  const [cleaning, setCleaning] = useState<boolean>(false);
  // Client-side pagination — the portfolio can hold 2 500+ assessments and
  // rendering every row froze the page even after the payload was trimmed.
  const RISK_PAGE_SIZE = 50;
  const [page, setPage] = useState<number>(0);

  // ---- Date-range presets (per the date-filter playbook) ----------
  const applyPreset = (preset: 'this_year' | 'last_year' | 'last_12m' | 'ytd' | 'clear'): void => {
    const now = new Date();
    const iso = (d: Date): string => d.toISOString().slice(0, 10);
    if (preset === 'clear') { setFilterFrom(''); setFilterTo(''); return; }
    if (preset === 'this_year') {
      setFilterFrom(iso(new Date(now.getFullYear(), 0, 1)));
      setFilterTo(iso(new Date(now.getFullYear(), 11, 31)));
    } else if (preset === 'last_year') {
      setFilterFrom(iso(new Date(now.getFullYear() - 1, 0, 1)));
      setFilterTo(iso(new Date(now.getFullYear() - 1, 11, 31)));
    } else if (preset === 'last_12m') {
      const from = new Date(now); from.setMonth(from.getMonth() - 12);
      setFilterFrom(iso(from)); setFilterTo(iso(now));
    } else if (preset === 'ytd') {
      setFilterFrom(iso(new Date(now.getFullYear(), 0, 1)));
      setFilterTo(iso(now));
    }
  };

  // Load pickers once.
  useEffect(() => {
    void (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get<Array<{ id: string; name: string; trading_name?: string }>>('/clients'),
          api.get<Array<{ id: string; name: string; client_id?: string; site_reference?: string }>>('/sites?limit=10000&fields=picker'),
        ]);
        setClients(c.data || []);
        setSites(s.data || []);
      } catch (err) { console.warn('risk-page pickers load failed', err); }
    })();
  }, []);

  const sitesForClient = useMemo(() =>
    filterClientId ? sites.filter((s) => s.client_id === filterClientId) : sites,
    [sites, filterClientId]);

  // Keep URL in sync so drill-down links from the dashboard land users on
  // the filtered view and can also be shared.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (filterBand) next.set('band', filterBand); else next.delete('band');
    if (filterClientId) next.set('client_id', filterClientId); else next.delete('client_id');
    if (filterSiteId) next.set('site_id', filterSiteId); else next.delete('site_id');
    if (filterFrom) next.set('from', filterFrom); else next.delete('from');
    if (filterTo) next.set('to', filterTo); else next.delete('to');
    if (filterAbm) next.set('abm', filterAbm); else next.delete('abm');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filterBand, filterClientId, filterSiteId, filterFrom, filterTo, filterAbm]);

  const loadAll = async (): Promise<void> => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filterBand) p.set('band', filterBand);
      if (filterClientId) p.set('client_id', filterClientId);
      if (filterSiteId) p.set('site_id', filterSiteId);
      if (filterFrom) p.set('submitted_from', filterFrom);
      if (filterTo) p.set('submitted_to', filterTo);
      p.set('limit', '10000');
      const qs = p.toString();
      const [list, sum] = await Promise.all([
        api.get<Assessment[]>(`/risk/assessments${qs ? `?${qs}` : ''}`),
        api.get<Summary>(`/risk/assessments/summary${qs ? `?${qs}` : ''}`),
      ]);
      setItems(list.data);
      setSummary(sum.data);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to load risk assessments.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [filterBand, filterClientId, filterSiteId, filterFrom, filterTo]);

  /**
   * Client-side ABM match filter. Applied after the server-side query so operators
   * can quickly narrow down "which sites still need a human to fix their ABM tags".
   * Buckets mirror the AbmMatchBadge thresholds so what the badge says matches the filter.
   */
  const visibleItems = useMemo(() => {
    if (!filterAbm) return items;
    return items.filter((a) => {
      const hasUnknown = Array.isArray(a.abm_unknown_types) && a.abm_unknown_types.length > 0;
      const conf = typeof a.abm_match_confidence === 'number' ? a.abm_match_confidence : null;
      if (filterAbm === 'na') return !hasUnknown && conf === null;
      if (filterAbm === 'unknown') return hasUnknown;
      if (conf === null) return false;
      if (filterAbm === 'low') return conf < 40;
      if (filterAbm === 'review') return conf >= 40 && conf < 80;
      if (filterAbm === 'good') return conf >= 80;
      return true;
    });
  }, [items, filterAbm]);

  // Bucket counts drive the quick-chip badges next to the ABM filter.
  const abmCounts = useMemo(() => {
    const c = { unknown: 0, low: 0, review: 0, good: 0, na: 0 };
    for (const a of items) {
      const hasUnknown = Array.isArray(a.abm_unknown_types) && a.abm_unknown_types.length > 0;
      const conf = typeof a.abm_match_confidence === 'number' ? a.abm_match_confidence : null;
      if (hasUnknown) c.unknown += 1;
      else if (conf === null) c.na += 1;
      else if (conf < 40) c.low += 1;
      else if (conf < 80) c.review += 1;
      else c.good += 1;
    }
    return c;
  }, [items]);

  // Sites needing operator attention (unknown or <40% match) — surfaced as the primary quick-fix chip.
  const abmFixableCount = abmCounts.unknown + abmCounts.low;

  const pagedItems = useMemo(
    () => visibleItems.slice(page * RISK_PAGE_SIZE, page * RISK_PAGE_SIZE + RISK_PAGE_SIZE),
    [visibleItems, page],
  );
  const riskPageCount = Math.max(1, Math.ceil(visibleItems.length / RISK_PAGE_SIZE));
  useEffect(() => { setPage(0); }, [visibleItems.length]);

  const openDetail = async (id: string): Promise<void> => {
    setSelectedId(id);
    setDetail(null);
    setOverrideReason('');
    setOverrideScore('');
    try {
      const r = await api.get<Assessment>(`/risk/assessments/${id}`);
      setDetail(r.data);
      setOverrideBand((r.data.effective_band as Band) ?? 'medium');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to load assessment.';
      toast.error(msg);
    }
  };

  const closeDetail = (): void => { setSelectedId(null); setDetail(null); };

  const submitOverride = async (): Promise<void> => {
    if (!detail) return;
    if (overrideReason.trim().length < 8) {
      toast.error('Please provide a justification of at least 8 characters.');
      return;
    }
    setBusy(true);
    try {
      const payload: Record<string, unknown> = { band: overrideBand, reason: overrideReason.trim() };
      if (overrideScore.trim().length > 0) {
        const s = Number(overrideScore);
        if (Number.isFinite(s) && s >= 0 && s <= 100) payload.score = s;
      }
      await api.post(`/risk/assessments/${detail.id}/override`, payload);
      toast.success('Override recorded.');
      await openDetail(detail.id);
      await loadAll();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Override failed.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const clearOverride = async (): Promise<void> => {
    if (!detail) return;
    if (!window.confirm('Clear the underwriter override and revert to the computed rating?')) return;
    setBusy(true);
    try {
      await api.delete(`/risk/assessments/${detail.id}/override`);
      toast.success('Override cleared.');
      await openDetail(detail.id);
      await loadAll();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to clear override.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const recomputeAll = async (): Promise<void> => {
    setRecomputing(true);
    try {
      const r = await api.post<{ recomputed: number; created: number }>('/risk/assessments/recompute-all');
      toast.success(`Risk engine ran — ${r.data.created} new, ${r.data.recomputed} recomputed.`);
      await loadAll();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Recompute failed.';
      toast.error(msg);
    } finally {
      setRecomputing(false);
    }
  };

  const cleanupOrphans = async (): Promise<void> => {
    if (!window.confirm('Sweep any risk assessments whose source survey result no longer exists?')) return;
    setCleaning(true);
    try {
      const r = await api.post<{ scanned: number; orphans_removed: number; overrides_removed: number }>(
        '/risk/assessments/cleanup-orphans',
      );
      const { scanned, orphans_removed, overrides_removed } = r.data;
      if (orphans_removed > 0) {
        toast.success(`Removed ${orphans_removed} orphaned assessment${orphans_removed === 1 ? '' : 's'} (${overrides_removed} override${overrides_removed === 1 ? '' : 's'}). Scanned ${scanned}.`);
      } else {
        toast.info(`No orphans found. Scanned ${scanned} assessment${scanned === 1 ? '' : 's'}.`);
      }
      await loadAll();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Cleanup failed.';
      toast.error(msg);
    } finally {
      setCleaning(false);
    }
  };

  const totals = useMemo(() => summary ?? { total: 0, by_band: { low: 0, medium: 0, high: 0, critical: 0 } }, [summary]);

  // ---- Master-only hard delete ----
  const { user, role, impersonating } = useAuth();
  const isMasterActor = Boolean(impersonating || user?.context === 'master' || role?.key === 'master_admin');
  const [selIds, setSelIds] = useState<Set<string>>(new Set());
  const [delOpen, setDelOpen] = useState(false);
  const [delText, setDelText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const toggleSel = (id: string): void => setSelIds((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const doHardDelete = async (): Promise<void> => {
    setDeleting(true);
    try {
      const r = await api.post<{ count: number; deleted: Record<string, number> }>(
        '/master/hard-delete/risk-assessments', { ids: [...selIds], confirm: delText });
      toast.success(`Permanently deleted ${r.data.count} assessment(s) and ${r.data.deleted.survey_results} survey result(s)`);
      setSelIds(new Set()); setDelOpen(false); setDelText('');
      await loadAll();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Delete failed');
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="risk-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Risk Ratings</h2>
          <p className="text-sm text-raptor-navy/60 font-body">Every submitted survey is scored automatically. Underwriters can override any rating with a written justification, kept in the audit trail.</p>
        </div>
        <div className="flex gap-2">
          {isMasterActor && selIds.size > 0 && (
            <Button size="sm" variant="destructive" onClick={() => setDelOpen(true)} data-testid="risk-hard-delete-btn">
              <Trash2 size={14} /> Delete {selIds.size} selected
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={cleanupOrphans} disabled={cleaning} data-testid="cleanup-orphans-btn" title="Remove assessments whose source survey result no longer exists">
            <Eraser size={14} className={cleaning ? 'animate-pulse' : ''} /> Clean orphans
          </Button>
          <Button size="sm" variant="outline" onClick={recomputeAll} disabled={recomputing} data-testid="recompute-all-btn">
            <RefreshCcw size={14} className={recomputing ? 'animate-spin' : ''} /> Recompute all
          </Button>
        </div>
      </div>

      {/* KPI tiles by band */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-[11px] uppercase text-raptor-navy/60 font-body">Total scored</div>
            <div className="font-tabular text-3xl font-bold text-raptor-navy mt-1" data-testid="risk-total">{totals.total}</div>
          </CardContent>
        </Card>
        {(['low', 'medium', 'high', 'critical'] as Band[]).map((b) => (
          <Card key={b} className={`ring-1 ${BAND_STYLE[b].ring}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase text-raptor-navy/60 font-body">{BAND_STYLE[b].label}</div>
                <BandBadge band={b} />
              </div>
              <div className="font-tabular text-3xl font-bold text-raptor-navy mt-1" data-testid={`risk-count-${b}`}>{totals.by_band[b]}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <Label>Band</Label>
              <Select value={filterBand} onChange={(e) => setFilterBand(e.target.value)} data-testid="risk-filter-band">
                <option value="">All bands</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </Select>
            </div>
            <div>
              <Label>ABM match</Label>
              <Select value={filterAbm} onChange={(e) => setFilterAbm(e.target.value)} data-testid="risk-filter-abm">
                <option value="">All confidence levels</option>
                <option value="unknown">Unknown ABM ({abmCounts.unknown})</option>
                <option value="low">Low match &lt; 40% ({abmCounts.low})</option>
                <option value="review">Review 40–79% ({abmCounts.review})</option>
                <option value="good">Good match ≥ 80% ({abmCounts.good})</option>
                <option value="na">No ABM data ({abmCounts.na})</option>
              </Select>
            </div>
            <div>
              <Label>Client</Label>
              <SearchPicker<{ id: string; name: string; trading_name?: string }>
                items={clients}
                selectedId={filterClientId || null}
                onPick={(c) => { setFilterClientId(c?.id ?? ''); setFilterSiteId(''); }}
                labelFor={(c) => c.trading_name || c.name}
                sublabelFor={(c) => c.id}
                placeholder="All clients"
                testId="risk-filter-client"
              />
            </div>
            <div>
              <Label>Site</Label>
              <SearchPicker<{ id: string; name: string; site_reference?: string; client_id?: string }>
                items={sitesForClient}
                selectedId={filterSiteId || null}
                onPick={(s) => setFilterSiteId(s?.id ?? '')}
                labelFor={(s) => s.name}
                sublabelFor={(s) => [s.site_reference && `Ref ${s.site_reference}`, s.id].filter(Boolean).join(' · ')}
                placeholder={filterClientId ? 'All sites for this client' : 'All sites'}
                testId="risk-filter-site"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Calendar size={12} /> Inspection date</Label>
              <div className="grid grid-cols-2 gap-1">
                <Input
                  type="date"
                  value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                  data-testid="risk-filter-from"
                  aria-label="From"
                />
                <Input
                  type="date"
                  value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)}
                  data-testid="risk-filter-to"
                  aria-label="To"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center text-xs">
            <span className="text-raptor-navy/60 font-body mr-1">Quick range:</span>
            <button type="button" onClick={() => applyPreset('ytd')} className="px-2 py-1 rounded-md border border-border/60 hover:bg-raptor-blue/5 font-body" data-testid="preset-ytd">Year to date</button>
            <button type="button" onClick={() => applyPreset('this_year')} className="px-2 py-1 rounded-md border border-border/60 hover:bg-raptor-blue/5 font-body" data-testid="preset-this-year">This year</button>
            <button type="button" onClick={() => applyPreset('last_year')} className="px-2 py-1 rounded-md border border-border/60 hover:bg-raptor-blue/5 font-body" data-testid="preset-last-year">Last year</button>
            <button type="button" onClick={() => applyPreset('last_12m')} className="px-2 py-1 rounded-md border border-border/60 hover:bg-raptor-blue/5 font-body" data-testid="preset-last-12m">Last 12 months</button>
            <span className="mx-1 h-4 w-px bg-border/70" aria-hidden />
            <span className="text-raptor-navy/60 font-body mr-1">ABM:</span>
            {abmFixableCount > 0 && (
              <button
                type="button"
                onClick={() => setFilterAbm(abmCounts.unknown > 0 ? 'unknown' : 'low')}
                className="px-2 py-1 rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 font-body inline-flex items-center gap-1"
                data-testid="risk-filter-abm-fix"
                title="Show assessments where the ABM model could not be matched confidently"
              >
                Fix ABM matches
                <span className="font-tabular font-semibold">{abmFixableCount}</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setFilterAbm('good')}
              className={`px-2 py-1 rounded-md border font-body ${filterAbm === 'good' ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-border/60 hover:bg-raptor-blue/5'}`}
              data-testid="risk-filter-abm-good"
            >
              Good matches
            </button>
            <div className="flex-1" />
            {(filterBand || filterClientId || filterSiteId || filterFrom || filterTo || filterAbm) && (
              <button
                type="button"
                onClick={() => { setFilterBand(''); setFilterClientId(''); setFilterSiteId(''); setFilterAbm(''); applyPreset('clear'); }}
                className="px-2 py-1 rounded-md border border-red-200 text-red-700 hover:bg-red-50 font-body inline-flex items-center gap-1"
                data-testid="risk-filter-clear-all"
              ><Eraser size={11} /> Clear all</button>
            )}
            <span className="text-raptor-navy/60 font-tabular text-[11px]" data-testid="risk-filter-count">
              {visibleItems.length}{filterAbm ? ` of ${items.length}` : ''} result(s)
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Assessments table */}
      <Card>
        <CardHeader>
          <CardTitle>Portfolio assessments</CardTitle>
          <CardDescription>Each row represents one completed survey with its computed and effective rating.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm font-body">
            <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
              <tr>
                {isMasterActor && (
                  <th className="py-2 px-3 text-center w-8">
                    <input
                      type="checkbox"
                      aria-label="Select all on page"
                      checked={pagedItems.length > 0 && pagedItems.every((a) => selIds.has(a.id))}
                      onChange={(e) => setSelIds((prev) => {
                        const n = new Set(prev);
                        if (e.target.checked) pagedItems.forEach((a) => n.add(a.id));
                        else pagedItems.forEach((a) => n.delete(a.id));
                        return n;
                      })}
                      data-testid="risk-select-all"
                    />
                  </th>
                )}
                <th className="py-2 px-3 text-left">Assessment</th>
                <th className="py-2 px-3 text-left">Client</th>
                <th className="py-2 px-3 text-left">Site name</th>
                <th className="py-2 px-3 text-left">Template</th>
                <th className="py-2 px-3 text-left">Completed by</th>
                <th className="py-2 px-3 text-left">Date submitted</th>
                <th className="py-2 px-3 text-center">ABM match</th>
                <th className="py-2 px-3 text-right">Computed</th>
                <th className="py-2 px-3 text-right">Effective</th>
                <th className="py-2 px-3 text-left">Band</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={isMasterActor ? 11 : 10} className="py-6 text-center text-raptor-navy/50">Loading…</td></tr>
              )}
              {!loading && visibleItems.length === 0 && (
                <tr><td colSpan={isMasterActor ? 11 : 10} className="py-8 text-center text-raptor-navy/50">
                  No assessments match the current filters. Try a different date range or clear filters.
                </td></tr>
              )}
              {pagedItems.map((a) => (
                <tr key={a.id} className="border-t border-border/60 hover:bg-raptor-neutral/20" data-testid={`risk-row-${a.id}`}>
                  {isMasterActor && (
                    <td className="py-2 px-3 text-center">
                      <input type="checkbox" checked={selIds.has(a.id)} onChange={() => toggleSel(a.id)} data-testid={`risk-select-${a.id}`} />
                    </td>
                  )}
                  <td className="py-2 px-3 font-tabular text-xs">{a.id}</td>
                  <td className="py-2 px-3">
                    <div className="font-semibold text-raptor-navy">{a.client_name || '—'}</div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="text-raptor-navy">{a.site_name || <span className="italic text-raptor-navy/40">unnamed</span>}</div>
                    {a.site_reference && (
                      <div className="text-[11px] text-raptor-navy/60 font-tabular">Ref {a.site_reference}</div>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <div>{a.template_name}</div>
                    <div className="text-[11px] text-raptor-navy/50 font-tabular">v{a.template_version}</div>
                  </td>
                  <td className="py-2 px-3 text-xs">
                    {a.surveyor_name || <span className="italic text-raptor-navy/40">—</span>}
                  </td>
                  <td className="py-2 px-3 text-[11px] text-raptor-navy/70 font-tabular whitespace-nowrap">
                    {a.submitted_at
                      ? new Date(a.submitted_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
                      : <span className="italic text-raptor-navy/40">—</span>}
                  </td>
                  <td className="py-2 px-3 text-center">
                    <AbmMatchBadge confidence={a.abm_match_confidence} model={a.abm_resolved_model} unknown={a.abm_unknown_types} />
                  </td>
                  <td className="py-2 px-3 text-right font-tabular">{a.normalised_score.toFixed(1)}</td>
                  <td className="py-2 px-3 text-right font-tabular">
                    <span className={a.is_overridden ? 'text-raptor-blue font-semibold' : ''}>{a.effective_score.toFixed(1)}</span>
                    {a.is_overridden && <span className="ml-1 text-[10px] text-raptor-blue">(override)</span>}
                  </td>
                  <td className="py-2 px-3"><BandBadge band={a.effective_band} /></td>
                  <td className="py-2 px-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => openDetail(a.id)} data-testid={`risk-open-${a.id}`}>
                      <Info size={14} /> Review
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {visibleItems.length > RISK_PAGE_SIZE && (
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-border/60 text-xs font-body" data-testid="risk-pager">
              <span className="text-raptor-navy/60">
                Showing {page * RISK_PAGE_SIZE + 1}–{Math.min((page + 1) * RISK_PAGE_SIZE, visibleItems.length)} of {visibleItems.length}
              </span>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="risk-prev-page">Prev</Button>
                <span className="px-2 text-raptor-navy/70 font-tabular">Page {page + 1} / {riskPageCount}</span>
                <Button size="sm" variant="outline" disabled={page + 1 >= riskPageCount} onClick={() => setPage((p) => Math.min(riskPageCount - 1, p + 1))} data-testid="risk-next-page">Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Master-only hard-delete confirm */}
      {delOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="risk-delete-modal">
          <Card className="w-full max-w-md border-red-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700"><Trash2 size={18} /> Permanently delete {selIds.size} assessment(s)</CardTitle>
              <CardDescription>
                This hard-deletes the selected risk ratings <b>and their source survey results</b> (plus overrides, photos and signatures). It cannot be undone and is recorded in the audit log. Type <b>DELETE</b> to confirm.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input value={delText} onChange={(e) => setDelText(e.target.value)} placeholder="Type DELETE" data-testid="risk-delete-confirm-input" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setDelOpen(false); setDelText(''); }} data-testid="risk-delete-cancel">Cancel</Button>
                <Button variant="destructive" onClick={() => void doHardDelete()} disabled={deleting || delText.trim() !== 'DELETE'} data-testid="risk-delete-confirm">
                  {deleting ? 'Deleting…' : 'Delete permanently'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Detail side panel */}
      {selectedId !== null && (
        <div className="fixed inset-0 z-50 flex" data-testid="risk-detail-panel">
          <div
            className="flex-1 bg-black/40 backdrop-blur-sm"
            onClick={closeDetail}
            aria-label="Close panel"
            data-testid="risk-detail-backdrop"
          />
          <div className="w-full max-w-2xl bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
            <div className="sticky top-0 bg-white border-b border-border/70 p-4 flex items-start justify-between z-10">
              <div className="min-w-0 pr-3">
                <div className="flex items-center gap-2 font-headline text-lg text-raptor-navy" data-testid="risk-detail-heading">
                  <ShieldAlert size={18} className="text-raptor-blue shrink-0" />
                  <span className="truncate">
                    {detail?.site_name
                      ? detail.site_name
                      : detail?.site_reference
                        ? `Site ${detail.site_reference}`
                        : 'Risk assessment'}
                  </span>
                </div>
                <div className="text-[11px] text-raptor-navy/60 font-tabular mt-0.5 flex items-center gap-2 flex-wrap">
                  {detail?.client_name && <span className="font-semibold text-raptor-navy/80">{detail.client_name}</span>}
                  {detail?.site_reference && detail?.site_name && (
                    <span className="text-raptor-navy/50">· Ref {detail.site_reference}</span>
                  )}
                  {detail?.id && <span className="text-raptor-navy/40">· {detail.id}</span>}
                </div>
                <div className="text-xs text-raptor-navy/60 font-body mt-1">
                  Review the factors that drove this score and, if required, record a written underwriter override.
                </div>
              </div>
              <Button size="icon" variant="ghost" onClick={closeDetail} data-testid="risk-detail-close" aria-label="Close">
                <X size={18} />
              </Button>
            </div>

            {!detail && <div className="p-6 text-sm text-raptor-navy/60">Loading assessment…</div>}
            {detail && (
              <div className="p-6 space-y-6">
                {/* Geofence bypass banner — surveyor was outside the
                    permitted radius when they submitted this survey. */}
                {detail.geofence_bypass && (
                  <div className="rounded-md border border-red-300 bg-red-50 p-3 flex items-start gap-3" data-testid="risk-geofence-bypass">
                    <MapPin size={16} className="text-red-700 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-headline text-red-800">
                        Surveyor was outside the geofence
                      </div>
                      <div className="text-xs text-red-800/80 font-body mt-1">
                        Recorded <b className="font-tabular">{Math.round(detail.geofence_bypass.distance_m).toLocaleString()} m</b> from the site (threshold <b className="font-tabular">{detail.geofence_bypass.threshold_m} m</b>).
                        {detail.geofence_bypass.acknowledged_at && (
                          <> Bypass acknowledged at {new Date(detail.geofence_bypass.acknowledged_at).toLocaleString()}.</>
                        )}
                      </div>
                      {detail.geofence_bypass.reason && (
                        <div className="mt-1.5 text-xs text-red-900 bg-white/60 rounded p-2 font-body">
                          <span className="font-semibold">Reason: </span>{detail.geofence_bypass.reason}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[11px] uppercase text-raptor-navy/60">Client</div>
                    <div className="font-semibold text-raptor-navy">{detail.client_name || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-raptor-navy/60">Site reference</div>
                    <div className="font-tabular">{detail.site_reference || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-raptor-navy/60">Template</div>
                    <div>{detail.template_name} <span className="text-[11px] text-raptor-navy/50 font-tabular">v{detail.template_version}</span></div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-raptor-navy/60">Computed at</div>
                    <div className="font-tabular text-xs">{new Date(detail.computed_at).toLocaleString()}</div>
                  </div>
                </div>

                <Card className="bg-raptor-neutral/30">
                  <CardContent className="p-4 grid grid-cols-3 gap-3">
                    <div>
                      <div className="text-[11px] uppercase text-raptor-navy/60">Computed score</div>
                      <div className="font-tabular text-2xl font-bold text-raptor-navy">{detail.normalised_score.toFixed(1)}</div>
                      <div className="mt-1"><BandBadge band={detail.computed_band} /></div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase text-raptor-navy/60">Effective score</div>
                      <div className={`font-tabular text-2xl font-bold ${detail.is_overridden ? 'text-raptor-blue' : 'text-raptor-navy'}`}>{detail.effective_score.toFixed(1)}</div>
                      <div className="mt-1"><BandBadge band={detail.effective_band} /></div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase text-raptor-navy/60">Raw / max</div>
                      <div className="font-tabular text-2xl font-bold text-raptor-navy/80">{detail.raw_score.toFixed(1)} / {detail.max_possible.toFixed(1)}</div>
                      <div className="text-[11px] text-raptor-navy/60 mt-1">weighted risk points</div>
                    </div>
                  </CardContent>
                </Card>

                <div>
                  <div className="font-semibold text-raptor-navy mb-2">Contributing factors</div>
                  {detail.breakdown && detail.engine === 'abm_site_v3' && (
                    <AbmBreakdownPanel breakdown={detail.breakdown} />
                  )}
                  {detail.factors.length === 0 && (
                    <div className="text-sm text-raptor-navy/60 p-3 bg-raptor-neutral/30 rounded-md">
                      No weighted questions have been configured for this template yet. Assign risk weights in the Template Editor to enable automated scoring.
                    </div>
                  )}
                  {detail.factors.length > 0 && (
                    <div className="border border-border rounded-md overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
                          <tr>
                            <th className="py-2 px-3 text-left">Question</th>
                            <th className="py-2 px-3 text-left">Answer</th>
                            <th className="py-2 px-3 text-right">Weight</th>
                            <th className="py-2 px-3 text-right">Contribution</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.factors.map((f) => (
                            <tr key={f.question_code} className={`border-t border-border/60 ${f.is_abm_row ? 'bg-raptor-blue/5' : (!f.passed ? 'bg-red-50/60' : '')}`}>
                              <td className="py-2 px-3">
                                <div className="text-raptor-navy inline-flex items-center gap-2">
                                  {f.question_text || f.question_code}
                                  {f.is_abm_row && typeof f.abm_match_confidence === 'number' && (
                                    <AbmMatchBadge confidence={f.abm_match_confidence} model={String(f.answer)} unknown={f.abm_unknown_types} />
                                  )}
                                </div>
                                <div className="text-[10px] text-raptor-navy/50 font-tabular">{f.question_code}{f.notes ? ` · ${f.notes}` : ''}</div>
                              </td>
                              <td className="py-2 px-3 font-tabular text-xs">
                                {String(f.answer)}
                                {f.is_abm_row && (
                                  <button
                                    type="button"
                                    className="ml-2 text-[10px] font-semibold text-raptor-blue hover:underline"
                                    onClick={() => setAbmFix({ assessmentId: detail.id, current: String(f.answer || '') })}
                                    data-testid="fix-abm-inline"
                                  >Fix ABM →</button>
                                )}
                              </td>
                              <td className="py-2 px-3 text-right font-tabular">{f.weight.toFixed(1)}</td>
                              <td className="py-2 px-3 text-right font-tabular">
                                {f.contribution > 0 ? (
                                  <span className="text-red-600">+{f.contribution.toFixed(1)}</span>
                                ) : (
                                  <span className="text-emerald-700">0.0</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Override form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Underwriter override</CardTitle>
                    <CardDescription>A written justification is required and will be recorded in the audit trail.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>New band</Label>
                        <Select value={overrideBand} onChange={(e) => setOverrideBand(e.target.value as Band)} data-testid="override-band">
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </Select>
                      </div>
                      <div>
                        <Label>Score (optional, 0-100)</Label>
                        <Input type="number" min={0} max={100} value={overrideScore} onChange={(e) => setOverrideScore(e.target.value)} placeholder="Auto from band" data-testid="override-score" />
                      </div>
                    </div>
                    <div>
                      <Label>Justification (required)</Label>
                      <Textarea
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                        placeholder="Explain the underwriting rationale for this override. Minimum 8 characters."
                        rows={3}
                        data-testid="override-reason"
                      />
                      <div className="text-[11px] text-raptor-navy/50 mt-1">{overrideReason.trim().length}/8 minimum</div>
                    </div>
                    <div className="flex gap-2 justify-end flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => setViewSurvey(detail.id)} data-testid="view-survey-btn" title="See the survey exactly as it was answered">
                        <FileText size={14} /> View answers
                      </Button>
                      {detail.is_overridden && (
                        <Button variant="outline" size="sm" onClick={clearOverride} disabled={busy} data-testid="clear-override-btn">
                          <Undo2 size={14} /> Clear override
                        </Button>
                      )}
                      <Button variant="primary" size="sm" onClick={submitOverride} disabled={busy || overrideReason.trim().length < 8} data-testid="submit-override-btn">
                        <ShieldAlert size={14} /> Record override
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* History */}
                {detail.overrides && detail.overrides.length > 0 && (
                  <div>
                    <div className="font-semibold text-raptor-navy mb-2">Override history</div>
                    <div className="space-y-2">
                      {detail.overrides.map((o) => (
                        <Card key={o.id} className="border-raptor-blue/30">
                          <CardContent className="p-3 text-sm">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <BandBadge band={o.from_band} />
                                <span className="text-raptor-navy/50">→</span>
                                <BandBadge band={o.to_band} />
                                <span className="font-tabular text-xs text-raptor-navy/60">({o.from_score.toFixed(1)} → {o.to_score.toFixed(1)})</span>
                              </div>
                              <div className="text-[11px] text-raptor-navy/60 font-tabular">{new Date(o.created_at).toLocaleString()}</div>
                            </div>
                            <div className="mt-2 text-raptor-navy/80 whitespace-pre-wrap">{o.reason}</div>
                            <div className="mt-2 text-[11px] text-raptor-navy/50 font-body">By {o.actor_email}</div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {abmFix && (
        <FixAbmDialog
          assessmentId={abmFix.assessmentId}
          currentAnswer={abmFix.current}
          matrixId="MTX-0002"
          onClose={() => setAbmFix(null)}
          onSaved={async (updated) => {
            setAbmFix(null);
            setDetail((prev) => prev ? { ...prev, ...updated } : prev);
            // Refresh list row so the column badge updates.
            void loadAll();
          }}
        />
      )}
      {viewSurvey && (
        <ViewSurveyDialog assessmentId={viewSurvey} onClose={() => setViewSurvey(null)} />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
 * ViewSurveyDialog — read-only view of the fully-answered survey behind
 * an assessment, so underwriters can see exactly what was submitted.
 * ═══════════════════════════════════════════════════════════════════════ */
interface SurveyView {
  template_name: string;
  template_version?: number;
  client_name?: string;
  site_name?: string;
  site_reference?: string;
  surveyor_name?: string;
  submitted_at?: string;
  answered_count: number;
  total_count: number;
  sections: Array<{
    code?: string; number?: string; title?: string; description?: string;
    questions: Array<{ code: string; number?: string; text: string; type?: string; answer: string; notes?: string; answered: boolean }>;
  }>;
  photos?: Array<{ id: string; tag?: string; caption?: string; data_url: string }>;
  videos?: Array<{ id: string; tag?: string; caption?: string; mime?: string; duration_sec?: number; size?: number; stream_url: string }>;
  signatures?: Array<{ id: string; role?: string; signer_name?: string; captured_at?: string; data_url?: string | null; selfie_data_url?: string | null }>;
  documents?: Array<{ id: string; filename: string; mime?: string; is_image?: boolean; data_url?: string | null; download_url: string }>;
}

const ViewSurveyDialog: React.FC<{ assessmentId: string; onClose: () => void }> = ({ assessmentId, onClose }) => {
  const [data, setData] = useState<SurveyView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  useEffect(() => {
    let cancel = false;
    setLoading(true); setError('');
    api.get<SurveyView>(`/risk/assessments/${assessmentId}/survey`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch((err: unknown) => {
        const d = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        if (!cancel) setError(d ?? 'Could not load the submitted survey.');
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [assessmentId]);

  const downloadPdf = async (): Promise<void> => {
    setDownloading(true);
    try {
      const r = await api.get(`/risk/assessments/${assessmentId}/survey/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      const base = `${data?.template_name || 'survey'}-${data?.site_reference || assessmentId}`;
      a.download = `${base.replace(/[^\w.-]+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not generate the PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
  const allPhotos = data?.photos || [];
  const openLightbox = (id: string): void => {
    const i = allPhotos.findIndex((p) => p.id === id);
    if (i >= 0) setLightboxIdx(i);
  };
  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setLightboxIdx(null);
      else if (e.key === 'ArrowRight') setLightboxIdx((i) => (i === null ? i : (i + 1) % allPhotos.length));
      else if (e.key === 'ArrowLeft') setLightboxIdx((i) => (i === null ? i : (i - 1 + allPhotos.length) % allPhotos.length));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, allPhotos.length]);
  // Match captured media to the question that produced it: media carry the
  // question_code in `tag`; we also fall back to the answer text containing
  // the media id (legacy rows). Matched media render inline next to the
  // question; anything unmatched still shows in the bottom galleries.
  const answerHasId = (answer: string, id: string): boolean => typeof answer === 'string' && answer.includes(id);
  const photosForQ = (code: string, answer: string) => (data?.photos || []).filter((p) => (p.tag && p.tag === code) || answerHasId(answer, p.id));
  const videosForQ = (code: string, answer: string) => (data?.videos || []).filter((v) => (v.tag && v.tag === code) || answerHasId(answer, v.id));
  const matchedPhotoIds = new Set<string>();
  const matchedVideoIds = new Set<string>();
  (data?.sections || []).forEach((sec) => sec.questions.forEach((q) => {
    photosForQ(q.code, q.answer).forEach((p) => matchedPhotoIds.add(p.id));
    videosForQ(q.code, q.answer).forEach((v) => matchedVideoIds.add(v.id));
  }));

  return (
    <div className="fixed inset-0 z-[300] bg-raptor-navy/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="view-survey-dialog">
      <Card className="w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="border-b border-border/60 shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <CardTitle className="flex items-center gap-2">
                <FileText size={18} className="text-raptor-blue" /> Submitted survey
              </CardTitle>
              <div className="text-xs text-raptor-navy/70 mt-1 font-body truncate">
                {data
                  ? <>{data.template_name}{data.template_version ? ` · v${data.template_version}` : ''} — {data.client_name}{data.site_reference ? ` · ${data.site_reference}` : ''}</>
                  : 'The answers exactly as they were submitted.'}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm" variant="outline"
                onClick={downloadPdf}
                disabled={loading || !!error || !data || downloading}
                data-testid="survey-download-pdf"
              >
                <Download size={14} className="mr-1.5" />
                {downloading ? 'Preparing…' : 'Download PDF'}
              </Button>
              <button onClick={onClose} className="text-raptor-navy/50 hover:text-raptor-navy transition-colors" data-testid="view-survey-close">
                <X size={18} />
              </button>
            </div>
          </div>
          {data && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
              <Badge variant="muted" className="font-tabular">{data.answered_count}/{data.total_count} answered</Badge>
              {data.surveyor_name && <span className="text-[11px] text-raptor-navy/60">Surveyor: {data.surveyor_name}</span>}
              {data.submitted_at && <span className="text-[11px] text-raptor-navy/60">Submitted {new Date(data.submitted_at).toLocaleString()}</span>}
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0 overflow-y-auto">
          {loading && <div className="p-10 text-center text-sm text-raptor-navy/60">Loading answers…</div>}
          {!loading && error && <div className="p-10 text-center text-sm text-red-500">{error}</div>}
          {!loading && !error && data && data.sections.length === 0 && (
            <div className="p-10 text-center text-sm text-raptor-navy/50">This survey has no sections.</div>
          )}
          {!loading && !error && data && data.sections.map((sec, si) => (
            <div key={sec.code || si} data-testid={`survey-section-${sec.code || si}`}>
              <div className="px-5 py-2.5 bg-raptor-neutral/40 border-y border-border/40">
                <div className="text-xs font-semibold uppercase tracking-wide text-raptor-navy/70">
                  {sec.number ? `${sec.number}. ` : ''}{sec.title || 'Section'}
                </div>
                {sec.description && <div className="text-[11px] text-raptor-navy/50 mt-0.5">{sec.description}</div>}
              </div>
              {sec.questions.length === 0 && <div className="px-5 py-3 text-xs text-raptor-navy/40">No questions in this section.</div>}
              {sec.questions.map((q, qi) => {
                const inlinePhotos = photosForQ(q.code, q.answer);
                const inlineVideos = videosForQ(q.code, q.answer);
                const hasMedia = inlinePhotos.length > 0 || inlineVideos.length > 0;
                const isMediaQ = q.type === 'photo_ref' || q.type === 'video_ref';
                return (
                <div key={q.code || qi} className="px-5 py-3 border-b border-border/30 grid grid-cols-1 sm:grid-cols-[1fr_1.1fr] gap-1 sm:gap-4 hover:bg-raptor-neutral/20 transition-colors">
                  <div className="text-sm text-raptor-navy/80">
                    {q.number && <span className="text-raptor-navy/40 font-tabular mr-1.5">{q.number}</span>}{q.text}
                  </div>
                  <div className="text-sm space-y-2">
                    {/* Hide the raw id text for media questions when we can show the media inline */}
                    {isMediaQ && hasMedia
                      ? null
                      : q.answered
                        ? <span className="text-raptor-navy font-medium whitespace-pre-wrap break-words">{q.answer}</span>
                        : <span className="text-raptor-navy/30 italic">Not answered</span>}
                    {inlinePhotos.length > 0 && (
                      <div className="grid grid-cols-2 gap-2" data-testid={`q-photos-${q.code}`}>
                        {inlinePhotos.map((p) => (
                          <button type="button" key={p.id} onClick={() => openLightbox(p.id)} className="block border border-border/50 rounded-md overflow-hidden bg-raptor-neutral/20 cursor-zoom-in" data-testid={`q-photo-${p.id}`}>
                            <img src={p.data_url} alt={p.caption || p.tag || 'survey photo'} className="w-full h-28 object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                    {inlineVideos.map((v) => (
                      <video key={v.id} src={`${BACKEND}${v.stream_url}`} controls playsInline preload="metadata"
                        className="w-full max-w-xs h-40 bg-black object-contain rounded-md border border-border/50"
                        data-testid={`q-video-${v.id}`} />
                    ))}
                    {q.notes && <div className="mt-1 text-[11px] text-raptor-navy/50 italic">Note: {q.notes}</div>}
                  </div>
                </div>
                );
              })}
            </div>
          ))}

          {!loading && !error && data && data.signatures && data.signatures.length > 0 && (
            <div data-testid="survey-signatures">
              <div className="px-5 py-2.5 bg-raptor-neutral/40 border-y border-border/40">
                <div className="text-xs font-semibold uppercase tracking-wide text-raptor-navy/70">Signatures</div>
              </div>
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {data.signatures.map((s) => (
                  <div key={s.id} className="border border-border/50 rounded-lg p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-raptor-blue">{s.role || 'Signatory'}</div>
                    <div className="text-sm font-semibold text-raptor-navy">{s.signer_name || '—'}</div>
                    {s.data_url
                      ? <img src={s.data_url} alt="signature" className="mt-2 max-h-20 w-auto border border-border/40 rounded bg-white p-1" />
                      : <div className="mt-2 text-xs text-raptor-navy/40 italic">Signature image unavailable</div>}
                    {s.selfie_data_url && (
                      <div className="mt-2">
                        <div className="text-[10px] uppercase tracking-wide text-raptor-navy/50 mb-1">Selfie</div>
                        <img src={s.selfie_data_url} alt="signer selfie" className="max-h-24 w-auto rounded border border-border/40 object-cover" data-testid={`signature-selfie-${s.id}`} />
                      </div>
                    )}
                    {s.captured_at && <div className="mt-1 text-[11px] text-raptor-navy/50">Captured {new Date(s.captured_at).toLocaleString()}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(() => {
            const galleryPhotos = (data?.photos || []).filter((p) => !matchedPhotoIds.has(p.id));
            return !loading && !error && data && galleryPhotos.length > 0 ? (
            <div data-testid="survey-photos">
              <div className="px-5 py-2.5 bg-raptor-neutral/40 border-y border-border/40">
                <div className="text-xs font-semibold uppercase tracking-wide text-raptor-navy/70">Photo gallery ({galleryPhotos.length})</div>
              </div>
              <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {galleryPhotos.map((p) => (
                  <button type="button" key={p.id} onClick={() => openLightbox(p.id)} className="border border-border/50 rounded-lg overflow-hidden bg-raptor-neutral/20 cursor-zoom-in text-left" data-testid={`gallery-photo-${p.id}`}>
                    <img src={p.data_url} alt={p.tag || 'survey photo'} className="w-full h-32 object-cover" />
                    {(p.tag || p.caption) && <div className="text-[11px] text-raptor-navy/70 px-2 py-1 truncate">{p.tag || p.caption}</div>}
                  </button>
                ))}
              </div>
            </div>
            ) : null;
          })()}
          {(() => {
            const galleryVideos = (data?.videos || []).filter((v) => !matchedVideoIds.has(v.id));
            return !loading && !error && data && galleryVideos.length > 0 ? (
            <div data-testid="survey-videos">
              <div className="px-5 py-2.5 bg-raptor-neutral/40 border-y border-border/40">
                <div className="text-xs font-semibold uppercase tracking-wide text-raptor-navy/70">Videos ({galleryVideos.length})</div>
              </div>
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {galleryVideos.map((v) => (
                  <div key={v.id} className="border border-border/50 rounded-lg overflow-hidden bg-raptor-neutral/20" data-testid={`survey-video-${v.id}`}>
                    <video
                      src={`${BACKEND}${v.stream_url}`}
                      controls playsInline preload="metadata"
                      className="w-full h-48 bg-black object-contain"
                      data-testid={`survey-video-player-${v.id}`}
                    />
                    <div className="flex items-center justify-between gap-2 px-2 py-1">
                      <span className="text-[11px] text-raptor-navy/70 truncate">{v.tag || v.caption || 'Survey video'}</span>
                      {v.duration_sec ? <span className="text-[11px] text-raptor-navy/45 font-tabular shrink-0">{Math.round(v.duration_sec)}s</span> : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            ) : null;
          })()}
          {!loading && !error && data && data.documents && data.documents.length > 0 && (
            <div data-testid="survey-documents">
              <div className="px-5 py-2.5 bg-raptor-neutral/40 border-y border-border/40">
                <div className="text-xs font-semibold uppercase tracking-wide text-raptor-navy/70">Documents ({data.documents.length})</div>
              </div>
              <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {data.documents.map((d) => {
                  const href = `${process.env.REACT_APP_BACKEND_URL || ''}${d.download_url}`;
                  return (
                    <a key={d.id} href={href} target="_blank" rel="noreferrer"
                      className="border border-border/50 rounded-lg overflow-hidden bg-raptor-neutral/20 hover:border-raptor-blue/50 block" data-testid={`survey-doc-${d.id}`}>
                      {d.is_image && d.data_url
                        ? <img src={d.data_url} alt={d.filename} className="w-full h-32 object-cover" />
                        : <div className="w-full h-32 flex items-center justify-center text-raptor-blue bg-raptor-blue/5"><FileText size={34} /></div>}
                      <div className="text-[11px] text-raptor-navy/70 px-2 py-1 truncate">{d.filename}</div>
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      {lightboxIdx !== null && allPhotos[lightboxIdx] && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxIdx(null)}
          data-testid="photo-lightbox"
        >
          <button type="button" onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2" data-testid="lightbox-close" aria-label="Close">
            <X size={28} />
          </button>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-sm font-tabular" data-testid="lightbox-counter">
            {lightboxIdx + 1} / {allPhotos.length}
            {allPhotos[lightboxIdx].tag ? <span className="ml-2 text-white/50">· {allPhotos[lightboxIdx].tag}</span> : null}
          </div>
          {allPhotos.length > 1 && (
            <button type="button" onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i === null ? i : (i - 1 + allPhotos.length) % allPhotos.length)); }}
              className="absolute left-3 sm:left-6 text-white/80 hover:text-white p-2" data-testid="lightbox-prev" aria-label="Previous">
              <ChevronLeft size={40} />
            </button>
          )}
          <img
            src={allPhotos[lightboxIdx].data_url}
            alt={allPhotos[lightboxIdx].caption || allPhotos[lightboxIdx].tag || 'survey photo'}
            className="max-h-[88vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />
          {allPhotos.length > 1 && (
            <button type="button" onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i === null ? i : (i + 1) % allPhotos.length)); }}
              className="absolute right-3 sm:right-6 text-white/80 hover:text-white p-2" data-testid="lightbox-next" aria-label="Next">
              <ChevronRight size={40} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
 * FixAbmDialog — inline ABM override picker for a single assessment
 * ═══════════════════════════════════════════════════════════════════════ */
interface AbmLibraryRow {
  id: string; new_name: string; old_name?: string; group?: string;
  type: 'notes' | 'coin'; capacity: number; active: boolean;
}

const FixAbmDialog: React.FC<{
  assessmentId: string; currentAnswer: string; matrixId: string;
  onClose: () => void; onSaved: (updated: Partial<Assessment>) => void | Promise<void>;
}> = ({ assessmentId, currentAnswer, matrixId, onClose, onSaved }) => {
  const [library, setLibrary] = useState<AbmLibraryRow[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get<{ library: AbmLibraryRow[] }>(`/risk/matrices/${matrixId}/abm-library`)
      .then((r) => setLibrary(r.data.library.filter((x) => x.active)))
      .catch(() => toast.error('Failed to load ABM library'));
  }, [matrixId]);
  const norm = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
  const filtered = React.useMemo(() => {
    const q = norm(query);
    if (!q) return library.slice(0, 60);
    return library.filter((r) => {
      const hay = norm(`${r.new_name} ${r.old_name || ''} ${r.group || ''}`);
      return hay.includes(q);
    }).slice(0, 60);
  }, [library, query]);
  const toggle = (name: string) =>
    setSelected((s) => s.includes(name) ? s.filter((x) => x !== name) : [...s, name]);
  const save = async () => {
    if (selected.length === 0) { toast.error('Pick at least one ABM row.'); return; }
    setSaving(true);
    try {
      const r = await api.post<Assessment>(`/risk/assessments/${assessmentId}/abm-override`, { abm_types: selected });
      toast.success('ABM override applied · assessment re-scored');
      await onSaved(r.data);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Save failed');
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[300] bg-raptor-navy/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="fix-abm-dialog">
      <Card className="w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle>Fix ABM assignment</CardTitle>
          <div className="text-xs text-raptor-navy/70 mt-1 font-body">
            Current answer: <span className="font-tabular">{currentAnswer || '—'}</span>.
            Pick the correct library row(s) — the assessment re-scores at 100% confidence.
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input placeholder="Search ABM by name, old name or group…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="fix-abm-search" autoFocus />
          <div className="max-h-[45vh] overflow-y-auto border border-border/60 rounded">
            {filtered.length === 0 && <div className="p-4 text-center text-xs text-raptor-navy/50">No matches</div>}
            {filtered.map((r) => (
              <button
                key={r.id} type="button"
                onClick={() => toggle(r.new_name)}
                className={`w-full text-left p-2 border-b border-border/40 flex items-center gap-2 text-xs transition-colors ${selected.includes(r.new_name) ? 'bg-raptor-blue/10' : 'hover:bg-raptor-neutral/40'}`}
                data-testid={`fix-abm-row-${r.id}`}
              >
                <input type="checkbox" checked={selected.includes(r.new_name)} readOnly />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-raptor-navy">{r.new_name}</div>
                  <div className="text-[10px] text-raptor-navy/50">{r.old_name} · {r.group} · {r.type} · {r.capacity.toLocaleString()} {r.type === 'coin' ? 'kg' : 'notes'}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button variant="gradient" onClick={() => void save()} disabled={saving || selected.length === 0} data-testid="fix-abm-save">
              {saving ? 'Applying…' : `Apply${selected.length ? ` (${selected.length})` : ''}`}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
